import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Lock, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { notify } from '@/components/ui/notify';
import { createPageUrl } from '@/utils';
import { AccountFinal } from '@/api/entities';
import { addMarketAccount, fetchMarketOpportunities, fetchPilotStatus } from '@/lib/marketExperienceApi';
import {
  accountNameInitials,
  extractAccountPageId,
  getOpportunityTypeMeta,
  isMarketAccountAdded,
  marketAddSuccessTitle,
  mktLabel,
  normalizeMarketAccountCode,
  opportunityTypeBadgeClass,
  pickTenantMarketAccountRow,
  resolveOpportunityType,
} from '@/lib/marketExperience';
import { isPilotAtCap, pilotErrorMessage } from '@/lib/pilotMode';
import { useTranslation } from '@/components/utils/translations';
import PilotTile, { TileEmpty, TileSkeleton } from './PilotTile';
import { markOnce } from './pilotPerf';
import { AccountLogo } from './pilotBadges';
import Hint from './pilotHint';
import { cleanQuotedText } from '@/lib/quoteText';

/* What each opportunity type means, in the engine's terms (marketIntel classifyCell). Shown with the
 * row's own why-now sentence, which says why THIS account is that type. */
const TYPE_MEANING = {
  renewal_recompete: 'The incumbent\'s contract in this category comes up for renewal or re-tender inside your horizon.',
  commercial_displacement: 'A competitor holds this category and there is evidence it could lose the account.',
  displacement_candidate: 'A competitor holds this category; a reason to switch is not yet on record.',
  homegrown_displacement: 'They run an in-house system for this today that a product could replace.',
  greenfield: 'No system is in place for this category yet.',
  expansion: 'An existing customer with room to grow into more of this category.',
  coexistence_adoption: 'An adjacent vendor is in place; this would sit alongside it.',
  addon: 'Already a customer in this category.',
};
const PRIORITY_MEANING = 'Your best openings right now: greenfield, displacement or expansion.';

const LIMIT = 5;
const CACHE_PREFIX = 'pilot-home-opps-v3:';
const CACHE_TTL_MS = 30 * 60 * 1000;
const KEEP = ['account_id', 'account_name', 'country', 'opportunity_type', 'market_score', 'queue', 'in_accounts', '_priority', 'logo_url', 'why_now', 'incumbent_vendor'];

/* Last list per tenant: a return visit paints at once, then the fresh list replaces it. Guarded —
 * storage can be absent or full. Accounts added here are dropped from it so they do not reappear. */
function readCache(tenantId) {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_PREFIX + tenantId) || 'null');
    return c && Date.now() - c.at < CACHE_TTL_MS ? c : null;
  } catch {
    return null;
  }
}

function writeCache(tenantId, rows, pilot) {
  try {
    const slim = rows.slice(0, 25).map((r) => Object.fromEntries(KEEP.map((k) => [k, r[k] ?? null])));
    localStorage.setItem(CACHE_PREFIX + tenantId, JSON.stringify({ at: Date.now(), rows: slim, pilot }));
  } catch {
    /* storage unavailable or full */
  }
}

/**
 * Pre-tender opportunities from Market: accounts in the tenant's market (Priority, then In your
 * market) that are not on their list yet. Priority first, then market score.
 */
async function loadOpportunities(tenantId) {
  const [priority, inMarket] = await Promise.all([
    fetchMarketOpportunities(tenantId, { queue: 'priority', limit: 50 }).catch(() => ({ rows: [] })),
    fetchMarketOpportunities(tenantId, { queue: 'in_market', limit: 50 }).catch(() => ({ rows: [] })),
  ]);
  const seen = new Set();
  const rows = [];
  for (const [isPriority, list] of [[true, priority?.rows || []], [false, inMarket?.rows || []]]) {
    for (const row of list) {
      if (!row?.account_id || seen.has(row.account_id) || isMarketAccountAdded(row)) continue;
      seen.add(row.account_id);
      rows.push({ ...row, _priority: isPriority || row.queue === 'priority' });
    }
  }
  return rows.sort((a, b) => (Number(b._priority) - Number(a._priority)) || ((b.market_score || 0) - (a.market_score || 0)));
}

async function resolveAccountRecordId(tenantId, row) {
  const code = normalizeMarketAccountCode(row.account_id, tenantId);
  const hits = code ? await AccountFinal.filter({ tenant_id: tenantId, account_id: code }) : [];
  const hit = pickTenantMarketAccountRow(hits || [], { tenantId, poolAccountId: row.account_id });
  return hit ? extractAccountPageId(hit) : null;
}

export default function NewOpportunitiesTile({ tenantId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [pilot, setPilot] = useState(null);
  const [added, setAdded] = useState({});
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!tenantId) return undefined;
    let cancelled = false;
    const cached = readCache(tenantId);
    setRows(cached ? cached.rows : null);
    setPilot(cached ? cached.pilot : null);
    setError(null);
    setAdded({});
    if (cached) markOnce('opps_cache_painted');
    Promise.all([loadOpportunities(tenantId), fetchPilotStatus(tenantId).catch(() => null)])
      .then(([list, status]) => {
        if (cancelled) return;
        setRows(list);
        setPilot(status);
        writeCache(tenantId, list, status);
        markOnce('opps_ready');
      })
      .catch((e) => {
        if (cancelled) return;
        if (!cached) setError(e?.message || 'Could not load opportunities');
      });
    return () => { cancelled = true; };
  }, [tenantId]);

  const atCap = isPilotAtCap(pilot);

  const handleAdd = async (row) => {
    setBusyId(row.account_id);
    try {
      const result = await addMarketAccount(tenantId, { account_id: row.account_id });
      const recordId = extractAccountPageId(result, Array.isArray(result?.added) ? result.added[0] : null);
      setAdded((m) => ({ ...m, [row.account_id]: recordId || true }));
      const c = readCache(tenantId);
      if (c) writeCache(tenantId, c.rows.filter((r) => r.account_id !== row.account_id), c.pilot);
      notify({ title: mktLabel(t, marketAddSuccessTitle(result)), variant: 'success' });
      fetchPilotStatus(tenantId).then(setPilot).catch(() => {});
    } catch (e) {
      notify({ title: t('mktCouldNotAddAccount'), description: pilotErrorMessage(e, t('mktPleaseTryAgain')), variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const handleView = async (row) => {
    setBusyId(row.account_id);
    try {
      const known = added[row.account_id];
      const id = typeof known === 'string' ? known : await resolveAccountRecordId(tenantId, row);
      if (!id) throw new Error(t('mktCouldNotResolveAccount'));
      navigate(createPageUrl(`AccountFinal?id=${encodeURIComponent(id)}`));
    } catch (e) {
      notify({ title: t('mktCouldNotOpenAccount'), description: e?.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const remaining = (rows || []).filter((r) => !added[r.account_id]).length;

  return (
    <PilotTile
      testId="pilot-tile-opportunities"
      icon={TrendingUp}
      title="New Opportunities"
      href={createPageUrl('Market')}
      count={rows ? remaining : null}
    >
      {rows == null && !error ? (
        <TileSkeleton />
      ) : error ? (
        <TileEmpty>{error}</TileEmpty>
      ) : rows.length === 0 ? (
        <TileEmpty>No new accounts in your market right now.</TileEmpty>
      ) : (
        rows.slice(0, LIMIT).map((row) => {
          const type = resolveOpportunityType(row);
          const meta = getOpportunityTypeMeta(type);
          const isAdded = Boolean(added[row.account_id]);
          const busy = busyId === row.account_id;
          return (
            <div key={row.account_id} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 rounded-lg p-2 hover:bg-gray-50">
              <AccountLogo
                url={row.logo_url}
                name={row.account_name}
                fallback={(
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-100">
                    <span className="text-[10px] font-semibold text-[#5C6B7A]">{accountNameInitials(row.account_name)}</span>
                  </div>
                )}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[#0A1A2F]">{row.account_name}</p>
                <p className="truncate text-xs text-[#5C6B7A]">{row.country || '—'}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {row._priority ? (
                    <Hint content={PRIORITY_MEANING}>
                      <Badge className="text-[10px] h-5 px-1.5 bg-[#00C2C2]/10 text-[#007A7A] border border-[#00C2C2]/30 hover:bg-[#00C2C2]/10">Priority</Badge>
                    </Hint>
                  ) : null}
                  {type ? (
                    <Hint content={(
                      <>
                        <p className="mb-1 font-semibold text-[#00C2C2]">{meta.label}</p>
                        {TYPE_MEANING[type] ? <p>{TYPE_MEANING[type]}</p> : null}
                        {row.why_now ? <p className="mt-1.5">{cleanQuotedText(row.why_now)}</p> : null}
                      </>
                    )}>
                      <Badge className={`text-[10px] h-5 px-1.5 ${opportunityTypeBadgeClass(type)}`}>{meta.label}</Badge>
                    </Hint>
                  ) : null}
                </div>
              </div>
              <div className="basis-full pl-11 sm:basis-auto sm:pl-0 flex-shrink-0">
              {isAdded ? (
                <button
                  type="button"
                  onClick={() => handleView(row)}
                  disabled={busy}
                  className="inline-flex h-8 w-32 flex-shrink-0 items-center justify-center gap-1 rounded-md border border-gray-200 bg-white text-xs font-medium text-[#0A1A2F] hover:bg-gray-50 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : null}
                  View Account
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleAdd(row)}
                  disabled={busy || atCap}
                  title={atCap ? 'Your pilot account limit is reached' : undefined}
                  className="inline-flex h-8 w-32 flex-shrink-0 items-center justify-center gap-1 rounded-md bg-[#00C2C2] text-xs font-medium text-white hover:bg-[#00A8A8] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : atCap ? <Lock className="w-3.5 h-3.5" aria-hidden="true" /> : null}
                  Add to Accounts
                </button>
              )}
              </div>
            </div>
          );
        })
      )}
    </PilotTile>
  );
}
